<template>
  <q-item
    class="customer-card__note q-py-md q-px-md"
    :disabled="isDisabled"
    multiline
  >
    <q-item-side>
      <user-avatar :user="author" height="38px" width="38px"/>
    </q-item-side>

    <q-item-main>
      <q-item-tile label lines="1">
        {{ noteAuthor }} {{ isOwner ? `(${$t('you')})` : ''}}
        <span v-show="note.is_private">&middot; {{ $t('private') }}</span>
      </q-item-tile>

      <q-item-tile sublabel class="q-mt-sm">
        <template v-if="!editing">
          <span class="pre-wrap">{{ note.text }}</span>

          <div class="lt-md q-mt-sm text-light">
            <span>
              {{ timestamp }}
              <q-tooltip anchor="top middle" self="bottom middle" :delay="300" :offset="[0, 5]">{{ date }}</q-tooltip>
            </span>
          </div>
        </template>

        <template v-else>
          <expandable-textarea
            ref="editTextarea"
            v-model="editText"
            :classes="['block', 'full-width']"
            :placeholder="$t('write_note')"
            expanded/>

          <div class="row justify-end q-mt-sm">
            <q-btn flat color="white" text-color="light" @click="handleCancelEdit">{{ $t('cancel') }}</q-btn>

            <q-btn flat color="white" text-color="faded" @click="handleSaveEdit">{{ $t('save') }}</q-btn>
          </div>
        </template>
      </q-item-tile>

      <div class="row items-center absolute text-light"
           style="margin-right: 24px; margin-top: 16px; top: 0; right: 0;">
        <span class="gt-sm">
          {{ timestamp }}
          <q-tooltip anchor="top middle" self="bottom middle" :delay="300" :offset="[0, 5]">{{ date }}</q-tooltip>
        </span>

        <q-icon v-if="isOwner" class="on-right" name="more_horiz" color="light" size="1.25rem">
          <q-popover>
            <q-list item-separator link>
              <!-- Toggle Private -->
              <q-item v-close-overlay @click.native="handleTogglePrivate">
                <q-item-main>
                  <span>{{ toggleVisibilityText }}</span>
                </q-item-main>
              </q-item>

              <q-item-separator/>

              <!-- Toggle Edit -->
              <q-item v-close-overlay @click.native="handleEdit">
                <q-item-side>
                  <q-icon name="edit"/>
                </q-item-side>

                <q-item-main>
                  <span>{{ $t('edit') }}</span>
                </q-item-main>
              </q-item>

              <!-- Delete -->
              <q-item v-close-overlay @click.native="handleDelete">
                <q-item-side>
                  <q-icon name="delete"/>
                </q-item-side>

                <q-item-main>
                  <span>{{ $t('delete') }}</span>
                </q-item-main>
              </q-item>
            </q-list>
          </q-popover>
        </q-icon>
      </div>
    </q-item-main>
  </q-item>
</template>

<script>
  import ExpandableTextarea from 'components/Common/Inputs/ExpandableTextarea.vue'
  import UserAvatar from 'components/Common/UserAvatar.vue'
  import { createErrorFromResponse } from 'src/utils/error'
  import { date } from 'quasar'
  import { distanceInWords } from 'src/utils/date'
  import imageMixin from 'src/utils/mixins/image'
  import { mapGetters } from 'vuex'
  import parseDate from 'date-fns/parse'

  export default {
    props: {
      note: {
        type: Object,
        required: true
      },

      disabled: {
        type: Boolean,
        default: false
      },

      updateNoteMethod: {
        type: Function,
        required: true
      },

      deleteNoteMethod: {
        type: Function,
        required: true
      }
    },

    data () {
      return {
        updating: false,
        editing: false,
        editText: null
      }
    },

    computed: {
      ...mapGetters(['user']),

      authorExists () {
        return this.note.user
      },

      author () {
        return this.note.user.data
      },

      noteAuthor () {
        return this.authorExists ? [this.author.first_name, this.author.last_name].join(' ').trim() : this.$t('deleted_user')
      },

      isOwner () {
        return this.authorExists ? this.author.id === this.user.id : false
      },

      timestamp () {
        return distanceInWords(this.note.created_at.date)
      },

      date () {
        return date.formatDate(parseDate(this.note.created_at.date), this.$t('date_time_format'))
      },

      toggleVisibilityText () {
        return this.note.is_private ? this.$t('make_public') : this.$t('make_private')
      },

      isDisabled () {
        return this.disabled || this.updating
      }
    },

    methods: {
      handleDelete () {
        this.updating = true

        this.deleteNoteMethod(this.note.id)
          .then(() => {
            this.updating = false
          })
          .catch(createErrorFromResponse)
      },

      handleTogglePrivate () {
        this.saveChanges({ is_private: !this.note.is_private })
          .catch(createErrorFromResponse)
      },

      handleEdit () {
        this.editText = this.note.text
        this.editing = true

        this.$nextTick(() => {
          this.$refs.editTextarea.focusInput()
        })
      },

      handleCancelEdit () {
        this.editing = false
        this.editText = null
      },

      handleSaveEdit () {
        this.updating = true

        this.saveChanges({ text: this.editText })
          .then(() => {
            this.editing = false
            this.editText = null
            this.updating = false
          })
          .catch(createErrorFromResponse)
      },

      saveChanges (data) {
        return this.updateNoteMethod(this.note.id, data)
      }
    },

    mixins: [
      imageMixin
    ],

    components: {
      ExpandableTextarea,
      UserAvatar
    }
  }
</script>
